// LD-4 — useLadderHistory hook.
//
// Reads past 20 LADDER cycles via get_ld_history RPC.
// Returns cycle summaries with rung-level results.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface LdHistoryRung {
  rung_code: string;
  rung_label: string;
  actual_odds: number;
  status: string;
  selections_won: number;
  selections_lost: number;
}

export interface LdHistoryCycle {
  id: string;
  cycle_type: string;
  status: string;
  fixture_window_start: string;
  pool_size: number | null;
  pool_avg_clv: number | null;
  created_at: string;
  rungs: readonly LdHistoryRung[];
}

export interface UseLadderHistoryState {
  cycles: readonly LdHistoryCycle[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Hook ─────────────────────────────────────────────────

export function useLadderHistory(): UseLadderHistoryState {
  const { user } = useAuthSession();
  const [cycles, setCycles] = useState<readonly LdHistoryCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setCycles([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: rpcError } = await (
          client.rpc as unknown as (fn: string) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        )("get_ld_history");

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setCycles([]);
          setLoading(false);
          return;
        }

        const raw = data as Record<string, unknown>;
        if (Array.isArray(raw.cycles)) {
          setCycles(
            (raw.cycles as Record<string, unknown>[]).map((c) => ({
              id: String(c.id ?? ""),
              cycle_type: String(c.cycle_type ?? "weekend"),
              status: String(c.status ?? ""),
              fixture_window_start: String(c.fixture_window_start ?? ""),
              pool_size: typeof c.pool_size === "number" ? c.pool_size : null,
              pool_avg_clv: typeof c.pool_avg_clv === "number" ? c.pool_avg_clv : null,
              created_at: String(c.created_at ?? ""),
              rungs: Array.isArray(c.rungs)
                ? (c.rungs as Record<string, unknown>[]).map((r) => ({
                    rung_code: String(r.rung_code ?? "R1"),
                    rung_label: String(r.rung_label ?? ""),
                    actual_odds: Number(r.actual_odds ?? 0),
                    status: String(r.status ?? "pending"),
                    selections_won: Number(r.selections_won ?? 0),
                    selections_lost: Number(r.selections_lost ?? 0),
                  }))
                : [],
            })),
          );
        } else {
          setCycles([]);
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  return useMemo(
    () => ({ cycles, loading, error, refetch }),
    [cycles, loading, error, refetch],
  );
}
